import { effect, inject, Injectable, signal } from '@angular/core';
import { environment } from 'src/environments/environment';
import { DataChatService } from './dataChat.service';
import { FillerType, getFillerFallback } from './fillerPhrases.i18n';
import { TranslationService } from 'src/app/services';

const SILENT_WAV_DATA_URI = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=';

// @i18n-values — claves resueltas dinámicamente en getPhrases()
const VOICE_KEYS: Record<FillerType, string> = {
    listening: 'voice.listening',
    thinking: 'voice.thinking',
    welcome: 'voice.welcome',
};

@Injectable({
    providedIn: 'root'
})
export class LocalFillerPhrasesService {
    private dataChat = inject(DataChatService);
    private _translationService = inject(TranslationService);
    
    private lastIndex = -1;
    private sharedAudio: HTMLAudioElement | null = null;
    private audioUnlocked = false;
    private unlockInFlight = false;
    protected readonly lang = this._translationService.selectedLanguage;

    public isPlayingWelcome = signal(false);
    private welcomeAudio;

    listenAbortEffect = effect((onCleanup) => {
        const controller = this.dataChat.abortSignal();
        const onAbort = () => {
            this.isPlayingWelcome.set(false);
            try { 
                this.sharedAudio?.pause(); 
                this.welcomeAudio?.pause() 
            } catch {}
        };

        controller.signal.addEventListener('abort', onAbort);
        onCleanup(() => {
            controller.signal.removeEventListener('abort', onAbort);
        });
    });

    // HRS-2948: las frases vienen de las claves "voice.*" de los langs (assets/i18n + CMS);
    // si la clave falta o el idioma no ha cargado, instant() devuelve la clave (string) y se
    // usa el respaldo local — al TTS nunca puede llegar una clave cruda.
    private getPhrases(type: FillerType): string[] {
        const lang = this.lang();
        const result = this._translationService.instant(VOICE_KEYS[type]);
        const fromLangs = Array.isArray(result)
            && result.length > 0
            && result.every((p: unknown) => typeof p === 'string' && p.trim().length > 0);

        console.debug(`🗣️ [LocalFiller] phrases type=${type} lang=${lang} source=${fromLangs ? 'langs' : 'local'}`);
        return fromLangs ? result : getFillerFallback(lang, type);
    }

    public sayListeningFiller() {
        this.playFiller(this.getPhrases('listening'), 'listening');
    }

    public unlockAudioFromGesture(): void {
        if (this.audioUnlocked || this.unlockInFlight)
            return;

        this.unlockInFlight = true;
        const audio = this.getSharedAudio();
        audio.muted = true;
        audio.volume = 0;
        audio.src = SILENT_WAV_DATA_URI;

        const unlockPromise = audio.play();
        if (!unlockPromise || typeof unlockPromise.then !== 'function') {
            this.unlockInFlight = false;
            audio.muted = false;
            audio.volume = 1;
            return;
        }

        unlockPromise
            .then(() => {
                this.audioUnlocked = true;
            })
            .catch((err) => {
                console.warn('[LocalFiller] iOS audio unlock blocked:', err);
            })
            .finally(() => {
                try {
                    audio.pause();
                    audio.currentTime = 0;
                } catch {}
                audio.muted = false;
                audio.volume = 1;
                this.unlockInFlight = false;
            });
    }

    public playTypingSound() {
        const audio = this.getSharedAudio();
        this.resetAudioState(audio);
        audio.src = `${environment.apiAssistantUrlBase}/static/typing_sound.mp3?t=${Date.now()}`;
        audio.volume = 0.4;
        audio.play().catch(e => console.warn('⚠️ [LocalFiller] SFX typing error:', e));
    }

    public playWelcome(): Promise<void> {
        // HRS-2398/A: the welcome greeting used to interpolate the tenant slug
        // (`¡Hola desde ${rest.subDomain}!`) so the voice literally said
        // "hola desde mirutavisitmadrid". Use a neutral translated greeting
        // instead (same language handling as the other fillers).
        const welcome = this.getPhrases('welcome');
        if (!welcome?.length)
            return Promise.resolve();

        this.isPlayingWelcome.set(true);
        let index: number;
        do {
            index = Math.floor(Math.random() * welcome.length);
        } while (index === this.lastIndex && welcome.length > 1);

        this.lastIndex = index;
        const phrase = welcome[index];

        const userLanguage = this.lang();
        const service = this.dataChat.id();
        const url =
            `${environment.apiAssistantUrlBase}/tts/simple` +
            `?text=${encodeURIComponent(phrase)}` +
            `&language=${encodeURIComponent(userLanguage)}` +
            `&t=${Date.now()}` +
            `&service=${service}`;

        const audio = new Audio(url);
        this.welcomeAudio = audio;

        return new Promise<void>((resolve, reject) => {
            const cleanup = () => {
                audio?.pause();
                audio.removeEventListener("ended", onEnded);
                audio.removeEventListener("error", onError);
                audio.removeEventListener("abort", onError);
                audio.removeEventListener("stalled", onError);
                audio.removeEventListener("emptied", onError);
            };

            const finishOk = (error?) => {
                this.isPlayingWelcome.set(false);
                if (error)
                    console.error("Audio Welcome Error: ", error);
                
                cleanup();
                resolve();
            };

            const finishKo = (error?) => {
                this.isPlayingWelcome.set(false);
                if (error)
                    console.error("Audio Welcome Error: ", error);
                
                cleanup();
                reject();
            };

            const onEnded = () => finishOk();
            const onError = () => finishKo(new Error("Audio error / cannot be played"));

            audio.addEventListener("ended", onEnded, { once: true });
            audio.addEventListener("error", onError, { once: true });
            audio.addEventListener("abort", onError, { once: true });

            audio.play()
                .then(() => {
                    console.log(`🗣️ [LocalFiller] Playing (welcome): ${phrase}`);
                })
                .catch((err) => {
                    console.warn("⚠️ [LocalFiller] Error playing audio filler:", err);
                    finishKo(err);
                });
        });
    }

    private playFiller(phrases: string[], type: FillerType) {
        if (!phrases?.length) 
            return;

        let index;
        do {
            index = Math.floor(Math.random() * phrases.length);
        } while (index === this.lastIndex && phrases.length > 1);

        this.lastIndex = index;
        const phrase = phrases[index];

        const userLanguage = this.lang();
        const url = `${environment.apiAssistantUrlBase}/tts/simple?text=${encodeURIComponent(phrase)}&language=${encodeURIComponent(userLanguage)}&t=${Date.now()}`;

        const audio = this.getSharedAudio();
        this.resetAudioState(audio);
        audio.src = url;
        audio.volume = 1;

        audio.play().catch(err => {
            console.warn('⚠️ [LocalFiller] Error playing audio filler:', err);
        });

        if (type === 'thinking') {
            audio.onended = () => {
                audio.src = `${environment.apiAssistantUrlBase}/static/typing_sound.mp3?t=${Date.now()}`;
                audio.volume = 0.4;
                audio.play().catch(e => console.warn('⚠️ [LocalFiller] SFX error:', e));
            };
        }

        console.log(`🗣️ [LocalFiller] Playing (${type}): ${phrase}`);
    }

    private getSharedAudio(): HTMLAudioElement {
        if (!this.sharedAudio) {
            this.sharedAudio = new Audio();
            this.sharedAudio.preload = 'auto';
            this.sharedAudio.setAttribute('playsinline', 'true');
            this.sharedAudio.setAttribute('webkit-playsinline', 'true');
        }
        return this.sharedAudio;
    }

    private resetAudioState(audio: HTMLAudioElement): void {
        try { audio.pause(); } catch {}
        audio.onended = null;
        audio.onerror = null;
        try { audio.currentTime = 0; } catch {}
    }

}
